// TFVF Shell — the Total Fusion-branded wrapper around every training
// sub-view. Handles:
//   - Brand header (VoiceFabric > Total Fusion > Program)
//   - Sub-navigation (My Training / Playbook / Assets / Manager / Admin)
//   - Injecting the .tfvf-scope CSS variables palette
//
// Sub-pages render inside the <div className="tfvf-scope">…</div>
// wrapper and inherit the palette + prose styles.
import React from 'react';
import { NavLink } from 'react-router-dom';
import { GraduationCap, BookOpen, Users, FolderOpen, Settings, ChevronRight, PlayCircle, ClipboardCheck, Award, Zap } from 'lucide-react';
import './tfvf.css';
import { useAuth } from '../../../contexts/AuthContext';

const NAV = [
  { key: 'home',     to: '/training',              label: 'My Training',       icon: GraduationCap },
  { key: 'practice', to: '/training/practice',     label: 'Practice Lab',      icon: PlayCircle },
  { key: 'cert',     to: '/training/certification',label: 'Certification',     icon: Award },
  { key: 'retraining',to:'/training/retraining',   label: 'Retraining',        icon: Zap },
  { key: 'practical',to: '/training/practical',    label: 'Practical',         icon: ClipboardCheck, managerOnly: true },
  { key: 'playbook', to: '/training/playbook',     label: 'Sales Playbook',    icon: BookOpen },
  { key: 'assets',   to: '/training/assets',       label: 'Asset Library',     icon: FolderOpen },
  { key: 'manager',  to: '/training/manager',      label: 'Manager Dashboard', icon: Users, managerOnly: true },
  { key: 'admin',    to: '/training/admin',        label: 'Content Admin',     icon: Settings, adminOnly: true },
];

const TfvfShell = ({ children }) => {
  const { user } = useAuth();
  const role = user?.role || '';
  const isManager = ['master_admin','admin','domain_admin','office_manager','domain_supervisor','supervisor','manager'].includes(role);
  const isAdmin   = ['master_admin','admin','domain_admin','office_manager'].includes(role);

  // Brand banner used to render here as a tall gold hero block. From
  // Feb 24 2026 the persistent tenant-portal masthead in `Layout.js`
  // provides the brand banner globally on every page — including this
  // one — so this shell no longer paints its own copy. Removing the
  // block keeps the Training page from stacking two gold bars. The
  // `.tf-hero` breadcrumb + subtitle below is intentionally kept —
  // it's separate content (VoiceFabric > Total Fusion > Program), not
  // a brand strip.

  return (
    <div className="tfvf-scope" data-testid="tfvf-shell">
      {/* Brand strip — light hero, dark ink text; item 8 flip. */}
      <div className="tf-hero px-6 py-8 sm:px-10">
        <div className="max-w-7xl mx-auto">
          <p className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--tf-gold)] font-bold flex items-center gap-2">
            VoiceFabric <ChevronRight className="w-3 h-3 opacity-70" /> Total Fusion <ChevronRight className="w-3 h-3 opacity-70" /> Sales Training, Coaching &amp; Behaviour Change
          </p>
          <h1 className="text-3xl sm:text-4xl font-bold mt-1 text-[color:var(--tf-forest)]">
            Total Fusion VoiceFabric Training
          </h1>
          <p className="mt-1 text-sm max-w-2xl text-[color:var(--tf-slate)]">
            Learn the approved question order, ALF, Green Brain and Rule of 3 — then practise, get assessed on real calls, and earn certification.
          </p>
        </div>
      </div>

      {/* Sub-nav */}
      <nav className="bg-white border-b border-[color:var(--tf-forest)]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 flex flex-wrap gap-1">
          {NAV.filter(n => (!n.managerOnly || isManager) && (!n.adminOnly || isAdmin)).map(n => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.key}
                to={n.to}
                end={n.key === 'home'}
                className={({ isActive }) => `inline-flex items-center gap-2 px-3 py-3 text-sm font-medium border-b-2 transition-colors ${
                  isActive ? 'border-[color:var(--tf-gold)] text-[color:var(--tf-forest)]' : 'border-transparent text-[color:var(--tf-muted)] hover:text-[color:var(--tf-forest)]'
                }`}
                data-testid={`tfvf-nav-${n.key}`}
              >
                <Icon className="w-4 h-4" /> {n.label}
              </NavLink>
            );
          })}
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-6 sm:py-8">
        {children}
      </main>
    </div>
  );
};

export default TfvfShell;
